import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EmailMessage } from '../models/email-message.model';
import { GenesysCloudPlatformApiService } from '../../../common/services/genesys-cloud-platform-api.service';
import { ConversationApiBase } from '../../../conversations/common/services/conversation-api-base.service';
import { ConversationResponse } from '../../../conversations/common/models/conversation-response.models';
import { CommunicationType } from '../../../conversations/common/models/communication-type.enum';
import { GenesysCloudPlatformAppsApiService } from 'common/store/genesys-cloud/common/services/genesys-cloud-platform-apps-api.service';
import { Attachment } from 'common/store/genesys-cloud/conversations/emails/models/attachment.model';
import { MessageDirection } from '../../messages/models/message-data.model';
import { ResourceRef } from 'common/store/genesys-cloud/common/models/resource-ref.model';

@Injectable({
  providedIn: 'root'
})
export class EmailConversationApiService extends ConversationApiBase {
  static readonly TransferConversationPath = (conversationId: string, participantId: string) =>
    `${ConversationApiBase.ConversationsPath}/emails/${conversationId}/participants/${participantId}/replace`;

  constructor(protected api: GenesysCloudPlatformApiService, private appsApi: GenesysCloudPlatformAppsApiService) {
    super(api, 'emails');
  }

  getEmailConversations$(): Observable<ConversationResponse[]> {
    return this.getConversations$(CommunicationType.Email);
  }

  getEmailMessage$(conversationId: string, emailMessageId: string): Observable<EmailMessage> {
    const path = `${ConversationApiBase.ConversationsPath}/emails/${conversationId}/messages/${emailMessageId}`;
    return this.api.get$<EmailMessage>(path);
  }

  sendEmailMessage$(conversationId: string, emailMessage: Partial<EmailMessage>): Observable<EmailMessage> {
    const path = `${ConversationApiBase.ConversationsPath}/emails/${conversationId}/messages`;
    return this.api.post$<EmailMessage>(path, emailMessage);
  }

  updateEmailDraft$(conversationId: string, draftMessage: Partial<EmailMessage>): Observable<EmailMessage> {
    const path = `${ConversationApiBase.ConversationsPath}/emails/${conversationId}/messages/draft`;
    return this.api.put$<EmailMessage>(path, draftMessage);
  }

  uploadAttachment$(conversationId: string, attachment: FormData): Observable<{ correlationId: string }> {
    return this.appsApi.post$(ConversationApiBase.UploadPath, attachment);
  }

  deleteAttachment$(conversationId: string, attachmentId: string): Observable<Response> {
    const path = `${ConversationApiBase.ConversationsPath}/emails/${conversationId}/messages/draft/attachments/${attachmentId}`;
    return this.api.delete$(path);
  }

  copyAttachmentToDraft$(
    conversationId: string,
    sourceMessageId: string,
    attachments: Attachment[]
  ): Observable<Response> {
    const path = `${ConversationApiBase.ConversationsPath}/emails/${conversationId}/messages/draft/attachments/copy`;
    return this.api.post$(path, { sourceMessage: { id: sourceMessageId }, attachments: attachments });
  }

  transferConversationToUser$(conversationId: string, participantId: string, userId: string): Observable<{}> {
    return this.api.post$(EmailConversationApiService.TransferConversationPath(conversationId, participantId), {
      userId
    });
  }

  transferConversationToQueue$(conversationId: string, participantId: string, queueId: string): Observable<{}> {
    return this.api.post$(EmailConversationApiService.TransferConversationPath(conversationId, participantId), {
      queueId
    });
  }

  createConversation$(queueId: string, toAddress: string): Observable<ResourceRef> {
    const path = `${ConversationApiBase.ConversationsPath}/emails`;
    return this.api.post$(path, {
      direction: MessageDirection.Outbound,
      queueId: queueId,
      toAddress: toAddress
    });
  }
}
